/**
 * Share-intent URLs for a blog post. Plain links — no platform SDKs, no CSP
 * change, nothing loads until the reader clicks. Pure so encoding is testable:
 * a raw `#` or `&` in a title silently truncates the shared text.
 *
 * Imports nothing from the UI layer: this module OWNS the icon-name union and
 * src/components/social-icons.tsx proves its glyph map covers it.
 */
export type SharePlatform = 'linkedin' | 'x' | 'facebook' | 'whatsapp';

/** Glyph names the share buttons need. Must be keys of SOCIAL_ICONS (enforced there). */
export type ShareIconName = 'LinkedIn' | 'Twitter' | 'Facebook' | 'WhatsApp';

export interface ShareLink {
  key: SharePlatform;
  /** Accessible label, e.g. "Share on LinkedIn". */
  label: string;
  /** Key into SOCIAL_ICONS (src/components/social-icons.tsx), which proves coverage of this union at compile time. */
  icon: ShareIconName;
  href: string;
}

export function buildShareLinks(url: string, title: string): ShareLink[] {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);
  // wa.me takes ONE text param: join before encoding; skip an empty title so
  // the message does not start with a space.
  const whatsappText = encodeURIComponent([title, url].filter(Boolean).join(' '));
  return [
    { key: 'linkedin', label: 'Share on LinkedIn', icon: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { key: 'x',        label: 'Share on X',        icon: 'Twitter',  href: `https://x.com/intent/tweet?url=${u}&text=${t}` },
    { key: 'facebook', label: 'Share on Facebook', icon: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { key: 'whatsapp', label: 'Share on WhatsApp', icon: 'WhatsApp', href: `https://wa.me/?text=${whatsappText}` },
  ];
}
